import { defineStore } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, helpers, maxValue, minValue } from '@vuelidate/validators'
import moment, { MomentInput } from 'moment'
import { computed } from 'vue'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { $t } from '@/composables/translation'
import calculateHoursFromStartAndEndTime from '@/components/Timesheet/utilities/calculateHoursFromStartAndEndTime'

export const useValidationStore = defineStore('timesheet-validation-store', () => {
  const timesheetStore = useTimesheetFormStore()

  const rules = computed(() =>
    Object.keys(timesheetStore.tableDateEntries).reduce(
      (accumulator, dateKey) => ({
        ...accumulator,
        [dateKey]: timesheetStore.isDateDisabled(dateKey)
          ? {}
          : {
              duration: timesheetStore.useDetailedView
                ? {}
                : {
                    maxValue: timesheetStore.useDayInputs
                      ? helpers.withMessage(
                          $t('Value must be less than or equal to 1 day'),
                          maxValue(1),
                        )
                      : helpers.withMessage(
                          $t('Value must be less than or equal to 24 hours'),
                          maxValue(24),
                        ),
                    minValue: helpers.withMessage(
                      $t('Value must be a positive number'),
                      minValue(0),
                    ),
                  },
              break: {
                lessThanTotal: helpers.withMessage(
                  $t('Value must be less than the day total'),
                  (value: number) => {
                    if (
                      !timesheetStore.tableDateEntries[dateKey].startTime ||
                      !timesheetStore.tableDateEntries[dateKey].endTime
                    ) {
                      return true
                    }
                    const totalDuration = calculateHoursFromStartAndEndTime(
                      timesheetStore.tableDateEntries[dateKey].startTime,
                      timesheetStore.tableDateEntries[dateKey].endTime,
                      0,
                    )
                    return value < totalDuration
                  },
                ),
                minValue: helpers.withMessage($t('Value must be a positive number'), minValue(0)),
              },
              startTime: {
                requiredIf: helpers.withMessage(
                  $t('Value is required if end date is present'),
                  requiredIf(() => timesheetStore.tableDateEntries[dateKey].endTime),
                ),
                beforeEndTime: helpers.withMessage(
                  $t('Value must be before the end time'),
                  (value: MomentInput) =>
                    !timesheetStore.tableDateEntries[dateKey].endTime ||
                    moment(value, 'HH:mm').isBefore(
                      moment(timesheetStore.tableDateEntries[dateKey].endTime, 'HH:mm'),
                    ),
                ),
              },
              endTime: {
                requiredIf: helpers.withMessage(
                  $t('Value is required if start date is present'),
                  requiredIf(() => timesheetStore.tableDateEntries[dateKey].startTime),
                ),
                afterStartTime: helpers.withMessage(
                  $t('Value must be after the start time'),
                  (value: MomentInput) =>
                    !timesheetStore.tableDateEntries[dateKey].startTime ||
                    moment(value, 'HH:mm').isAfter(
                      moment(timesheetStore.tableDateEntries[dateKey].startTime, 'HH:mm'),
                    ),
                ),
              },
            },
      }),
      {},
    ),
  )

  const v$ = useVuelidate(
    rules,
    computed(() => timesheetStore.tableDateEntries),
    {
      // $autoDirty: true,
    },
  )

  return {
    v$,
  }
})
